import { SelectableChip as LargeChip } from "@dropbox/dash-component-library";
import { Chip } from "@dropbox/dig-components/chip";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  EmailLine,
  PersonMultipleLine,
  SettingsLine,
} from "@dropbox/dig-icons/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import type { DashCustomizeSetupModuleAction } from "@mirage/analytics/events/enums/dash_customize_setup_module_action";
import { PAP_Select_DashCustomizeSetupModule } from "@mirage/analytics/events/types/select_dash_customize_setup_module";
import { useOnHideClick } from "@mirage/growth/onboarding/setup-module/shared/useOnHideClick";
import { useCanSeeInviteForm } from "@mirage/growth/team-invites/hooks/useCanSeeInviteForm";
import { InviteModal } from "@mirage/growth/team-invites/InviteModal";
import { Link } from "@mirage/link/Link";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { convertFeatureValueToBool } from "@mirage/service-experimentation/util";
import { useOnboardingValues } from "@mirage/service-onboarding/hooks";
import { openURL } from "@mirage/service-platform-actions";
import { DROPBOX_ADMIN_CONSOLE_URL } from "@mirage/shared/urls";
import { useModal } from "@mirage/shared/util/showModal";
import i18n, { Trans } from "@mirage/translations";
import { useCallback } from "react";
import { ModuleItemContainer } from "./shared/ModuleItemContainer";
import { useSetupModuleShown } from "./shared/useSetupModuleShown";
import styles from "./styles.module.css";

export const CustomizeModuleItem: React.FC = () => {
  const { reportPapEvent } = useMirageAnalyticsContext();
  const { onboardingValues, setOnboardingValue } = useOnboardingValues();
  const onHideClick = useOnHideClick("customize");
  useSetupModuleShown(
    "customize",
    !!onboardingValues?.dismissedSetupModuleCustomize,
  );

  const handleActionClick = useCallback(
    (dashCustomizeSetupModuleAction: DashCustomizeSetupModuleAction) => {
      reportPapEvent(
        PAP_Select_DashCustomizeSetupModule({
          dashActionSurface: "setup_module",
          actionSurfaceComponent: "setup_module",
          featureLine: "onboarding",
          dashCustomizeSetupModuleAction,
        }),
      );
    },
    [reportPapEvent],
  );

  const handleOnHideClick = () => {
    onHideClick(
      <Trans
        i18nKey={"setup_module_hide_customize"}
        components={{
          buttonLink: (
            <Link variant="monochromatic" href={DROPBOX_ADMIN_CONSOLE_URL} />
          ),
        }}
      />,
    );
    setOnboardingValue("dismissedSetupModuleCustomize", true);
  };

  if (onboardingValues?.dismissedSetupModuleCustomize) {
    return null;
  }

  return (
    <ModuleItemContainer
      label={i18n.t("setup_module_customize_title")}
      leftAccessory={PersonMultipleLine}
      onHideClick={handleOnHideClick}
      data-testid="setup-module-customize"
    >
      <Box className={styles.gap8} display="flex">
        <InviteChip onClick={() => handleActionClick("invite_team_members")} />
        <CustomizeChip onClick={() => handleActionClick("admin_console")} />
      </Box>
    </ModuleItemContainer>
  );
};

type ActionChipProps = {
  onClick: () => void;
};

const InviteChip = ({ onClick }: ActionChipProps) => {
  const { showModal, hideModal } = useModal();
  const startPageMemberInvitesFlag = convertFeatureValueToBool(
    useFeatureFlagValue("otc_growth_2025_01_30_start_page_member_invites"),
  );
  const canSeeInviteForm = useCanSeeInviteForm();
  const showInviteMembers = startPageMemberInvitesFlag && canSeeInviteForm;

  const handleOnClick = () => {
    onClick();
    showModal(<InviteModal onClose={hideModal} />);
  };

  if (!showInviteMembers) return null;

  return (
    <>
      <LargeChip onClick={handleOnClick}>
        <LargeChip.LargeIconAccessory>
          <UIIcon src={EmailLine} />
        </LargeChip.LargeIconAccessory>
        <Chip.Content>{i18n.t("invite_people")}</Chip.Content>
      </LargeChip>
    </>
  );
};

const CustomizeChip = ({ onClick }: ActionChipProps) => {
  const handleOnClick = () => {
    onClick();
    openURL(DROPBOX_ADMIN_CONSOLE_URL);
  };

  return (
    <LargeChip onClick={handleOnClick}>
      <LargeChip.LargeIconAccessory>
        <UIIcon src={SettingsLine} />
      </LargeChip.LargeIconAccessory>
      <Chip.Content>{i18n.t("customize")}</Chip.Content>
    </LargeChip>
  );
};
